import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Button,
    Chip,
    Paper,
    InputAdornment,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Fade,
    Skeleton,
    useTheme,
    alpha,
} from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { motion, AnimatePresence } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import VerifiedIcon from "@mui/icons-material/Verified";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ClearIcon from "@mui/icons-material/Clear";
import SwanLogo from "../../assets/logos/swansolutions_logo.png";

/* ─────────────────────────────────────────────────────────────────────────────
   GLASSMORPHISM STYLE HELPERS
   ───────────────────────────────────────────────────────────────────────────── */
const glassCard = (theme) => ({
    background: alpha(theme.palette.background.paper, 0.72),
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.dark, 0.08)}, 
                inset 0 0 0 1px ${alpha("#fff", 0.08)}`,
});

const glassInput = (theme) => ({
    "& .MuiOutlinedInput-root": {
        background: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderRadius: 3,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            background: alpha(theme.palette.background.paper, 0.8),
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
        "&.Mui-focused": {
            background: alpha(theme.palette.background.paper, 0.92),
            boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.25)}, 
                        0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
    },
});

/* ─────────────────────────────────────────────────────────────────────────────
   HAPTIC BUTTON COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const HapticButton = React.forwardRef(({ children, sx, ...props }, ref) => {
    const [pressed, setPressed] = useState(false);

    return (
        <Button
            ref={ref}
            {...props}
            onMouseDown={() => setPressed(true)}
            onMouseUp={() => setPressed(false)}
            onMouseLeave={() => setPressed(false)}
            onTouchStart={() => setPressed(true)}
            onTouchEnd={() => setPressed(false)}
            sx={{
                ...sx,
                transform: pressed ? "scale(0.97)" : "scale(1)",
                transition: pressed
                    ? "transform 50ms ease-in-out"
                    : "transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
        >
            {children}
        </Button>
    );
});

/* ─────────────────────────────────────────────────────────────────────────────
   DISTRICT LOGO COMPONENT - Handles all image types with fallback
   ───────────────────────────────────────────────────────────────────────────── */
const DistrictLogo = ({ url, name, size = 100, sx = {} }) => {
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const defaultLogo = "/logos/fig_CROP.png";

    // Reset state when URL changes
    useEffect(() => {
        setHasError(false);
        setIsLoading(true);
    }, [url]);

    const handleLoad = useCallback(() => {
        setIsLoading(false);
        setHasError(false);
    }, []);

    const handleError = useCallback(() => {
        setIsLoading(false);
        setHasError(true);
    }, []);

    // Determine the image source - handle various URL formats
    const getImageSrc = () => {
        if (!url || hasError) return defaultLogo;

        // Handle data URIs directly
        if (url.startsWith("data:")) return url;

        // Handle relative URLs
        if (url.startsWith("/")) return url;

        // Handle blob URLs
        if (url.startsWith("blob:")) return url;

        // Handle regular URLs - ensure proper protocol
        if (url.startsWith("http://") || url.startsWith("https://")) return url;

        // If no protocol, assume https
        return `https://${url}`;
    };

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: size,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...sx,
            }}
        >
            {isLoading && (
                <Skeleton
                    variant="rounded"
                    width="80%"
                    height={size * 0.8}
                    animation="wave"
                    sx={{ position: "absolute", borderRadius: 2 }}
                />
            )}
            <Box
                component="img"
                src={getImageSrc()}
                alt={`${name} logo`}
                onLoad={handleLoad}
                onError={handleError}
                sx={{
                    maxHeight: size,
                    maxWidth: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    opacity: isLoading ? 0 : 1,
                    transition: "opacity 0.3s ease-in-out",
                    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.08))",
                }}
            />
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ANIMATED STAT CARD
   ───────────────────────────────────────────────────────────────────────────── */
const StatCard = ({ icon, value, label, delay = 0 }) => {
    const theme = useTheme();

    return (
        <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                duration: 0.6,
                delay,
                type: "spring",
                stiffness: 100
            }}
        >
            <Paper
                sx={{
                    ...glassCard(theme),
                    p: 3,
                    textAlign: "center",
                    borderRadius: 4,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: "linear-gradient(90deg, #21dbb8, #2269a6)",
                    },
                }}
            >
                <Box sx={{ color: "secondary.main", mb: 1 }}>{icon}</Box>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 900,
                        background: "linear-gradient(135deg, #003366 0%, #21dbb8 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    {value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
            </Paper>
        </motion.div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   DISTRICT CARD COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const DistrictCard = ({ district, index }) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{
                duration: 0.5,
                delay: index * 0.05,
                type: "spring",
                stiffness: 120,
                damping: 20,
            }}
        >
            <Card
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    height: "100%",
                    borderRadius: 4,
                    ...glassCard(theme),
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    transform: isHovered ? "translateY(-8px) scale(1.02)" : "translateY(0) scale(1)",
                    boxShadow: isHovered
                        ? `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}, 
                           0 0 0 1px ${alpha(theme.palette.secondary.main, 0.3)}`
                        : glassCard(theme).boxShadow,
                    transition: "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: "linear-gradient(90deg, #21dbb8, #2269a6, #21dbb8)",
                        backgroundSize: "200% 100%",
                        animation: isHovered ? "shimmer 2s infinite linear" : "none",
                    },
                    "@keyframes shimmer": {
                        "0%": { backgroundPosition: "200% 0" },
                        "100%": { backgroundPosition: "-200% 0" },
                    },
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        p: 2.5,
                        pb: 1.5,
                        background: alpha(theme.palette.grey[100], 0.5),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                >
                    <DistrictLogo
                        url={district.DistrictLogoUrl}
                        name={district.DistrictName}
                        size={90}
                    />
                </Box>

                {/* Content Section */}
                <CardContent sx={{ flex: 1, pt: 2, pb: 2.5, px: 2.5 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            fontSize: "1.05rem",
                            lineHeight: 1.3,
                            minHeight: 44,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {district.DistrictName}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main" }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {district.State}, {district.Country}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <PeopleIcon sx={{ fontSize: 18, color: "secondary.main" }} />
                        <Typography variant="body2" color="text.secondary">
                            <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
                                {district.NoOfUsers?.toLocaleString() || "—"}
                            </Box>{" "}
                            users
                        </Typography>
                    </Box>
                </CardContent>

                {/* Active Badge */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 2,
                        background: alpha("#21dbb8", 0.15),
                        backdropFilter: "blur(4px)",
                    }}
                >
                    <VerifiedIcon sx={{ fontSize: 14, color: "#0d9b7a" }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#0d9b7a" }}>
                        Active
                    </Typography>
                </Box>
            </Card>
        </motion.div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN DISTRICTS COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
export default function Districts() {
    const theme = useTheme();
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [viewMode, setViewMode] = useState("grid"); // "grid" or "table"

    useEffect(() => {
        fetch("https://azuresql.swancreate.com/api/the/schooldistrict")
            .then((res) => res.json())
            .then((data) => {
                const districtData = Array.isArray(data) ? data : data.districts || [];
                setDistricts(districtData);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Unique states for filter dropdown
    const states = useMemo(() =>
        Array.from(
            new Set(
                districts.filter((d) => d.IsActive).map((d) => d.State).filter(Boolean)
            )
        ).sort(),
        [districts]
    );

    // Filter only active and matching
    const filtered = useMemo(() =>
        districts
            .filter((d) => d.IsActive)
            .filter((d) =>
                d.DistrictName?.toLowerCase().includes(nameFilter.toLowerCase())
            )
            .filter((d) => !stateFilter || d.State === stateFilter),
        [districts, nameFilter, stateFilter]
    );

    // Stats calculation
    const stats = useMemo(() => {
        const activeDistricts = districts.filter((d) => d.IsActive);
        const totalUsers = activeDistricts.reduce((sum, d) => sum + (d.NoOfUsers || 0), 0);
        const uniqueStates = new Set(activeDistricts.map((d) => d.State).filter(Boolean)).size;
        return {
            totalDistricts: activeDistricts.length,
            totalUsers,
            uniqueStates
        };
    }, [districts]);

    // MUI DataGrid Pro columns
    const columns = useMemo(() => [
        {
            field: "logo",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", py: 1 }}>
                    <DistrictLogo
                        url={params.row.DistrictLogoUrl}
                        name={params.row.DistrictName}
                        size={50}
                    />
                </Box>
            ),
        },
        {
            field: "DistrictName",
            headerName: "District Name",
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SchoolIcon sx={{ fontSize: 18, color: "secondary.main" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "State",
            headerName: "State",
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        background: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                    }}
                />
            ),
        },
        {
            field: "Country",
            headerName: "Country",
            width: 100,
        },
        {
            field: "NoOfUsers",
            headerName: "Users",
            width: 120,
            type: "number",
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PeopleIcon sx={{ fontSize: 16, color: "secondary.main" }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {params.value?.toLocaleString() || "—"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "IsActive",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Chip
                    icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                    label="Active"
                    size="small"
                    sx={{
                        fontWeight: 600,
                        background: alpha("#21dbb8", 0.15),
                        color: "#0d9b7a",
                        "& .MuiChip-icon": { color: "#0d9b7a" },
                    }}
                />
            ),
        },
    ], [theme]);

    const clearFilters = () => {
        setNameFilter("");
        setStateFilter("");
    };

    const hasActiveFilters = nameFilter || stateFilter;

    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh", pb: 8 }}>
            {/* ═══════════════════════════════════════════════════════════════════
                HERO SECTION
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                sx={{
                    width: "100vw",
                    position: "relative",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `
                        radial-gradient(ellipse at 20% 80%, rgba(33, 219, 184, 0.25) 0%, transparent 50%),
                        radial-gradient(ellipse at 80% 20%, rgba(34, 105, 166, 0.3) 0%, transparent 50%),
                        linear-gradient(135deg, #0b223e 0%, #145c8c 40%, #21dbb8 100%)
                    `,
                    minHeight: { xs: "55vh", md: "60vh" },
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
                        opacity: 0.03,
                        pointerEvents: "none",
                    },
                }}
            >
                {/* Animated background shapes */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 180, 360],
                    }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    sx={{
                        position: "absolute",
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        border: "1px solid rgba(255,255,255,0.08)",
                        top: -200,
                        right: -200,
                    }}
                />
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1.2, 1, 1.2],
                        rotate: [360, 180, 0],
                    }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    sx={{
                        position: "absolute",
                        width: 400,
                        height: 400,
                        borderRadius: "50%",
                        border: "1px solid rgba(255,255,255,0.05)",
                        bottom: -100,
                        left: -100,
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <Box
                            component="img"
                            src={SwanLogo}
                            alt="Swan Solutions"
                            sx={{
                                height: { xs: 70, md: 100 },
                                mb: 4,
                                filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.25))",
                            }}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                mb: 2,
                                fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.5rem" },
                                letterSpacing: "-0.03em",
                                textShadow: "0 4px 24px rgba(0,0,0,0.2)",
                                background: "linear-gradient(180deg, #fff 0%, rgba(255,255,255,0.85) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Trusted by Districts Nationwide
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                maxWidth: 700,
                                mx: "auto",
                                mb: 4,
                                color: "rgba(255,255,255,0.92)",
                                fontWeight: 400,
                                fontSize: { xs: "1.05rem", md: "1.2rem" },
                                lineHeight: 1.7,
                            }}
                        >
                            Empowering school systems of all sizes to lead with technology,
                            streamline operations, and deliver exceptional student outcomes.
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.8 }}
                    >
                        <HapticButton
                            variant="contained"
                            size="large"
                            href="/contact"
                            component={motion.a}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            sx={{
                                mt: 2,
                                borderRadius: 99,
                                fontWeight: 700,
                                px: 5,
                                py: 1.6,
                                fontSize: { xs: "1rem", md: "1.1rem" },
                                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
                                color: "#0b223e",
                                boxShadow: "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.5)",
                                "&:hover": {
                                    background: "#fff",
                                    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                                },
                            }}
                        >
                            Partner With Us
                        </HapticButton>
                    </motion.div>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                STATS SECTION
                ═══════════════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ mt: -8, position: "relative", zIndex: 2 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                        gap: 3,
                    }}
                >
                    <StatCard
                        icon={<SchoolIcon sx={{ fontSize: 40 }} />}
                        value={stats.totalDistricts.toLocaleString()}
                        label="Partner Districts"
                        delay={0.1}
                    />
                    <StatCard
                        icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                        value={stats.totalUsers.toLocaleString()}
                        label="Active Users"
                        delay={0.2}
                    />
                    <StatCard
                        icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
                        value={stats.uniqueStates}
                        label="States Served"
                        delay={0.3}
                    />
                </Box>
            </Container>

            {/* ═══════════════════════════════════════════════════════════════════
                FILTERS & VIEW TOGGLE
                ═══════════════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ mt: 6, mb: 4 }}>
                <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    sx={{
                        ...glassCard(theme),
                        p: 3,
                        borderRadius: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 2, flex: 1, flexWrap: "wrap" }}>
                            <TextField
                                label="Search districts"
                                value={nameFilter}
                                onChange={(e) => setNameFilter(e.target.value)}
                                sx={{ flex: 1, minWidth: 240, maxWidth: 400, ...glassInput(theme) }}
                                size="medium"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "text.secondary" }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: nameFilter && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setNameFilter("")}
                                                sx={{
                                                    "&:active": { transform: "scale(0.9)" },
                                                    transition: "transform 100ms ease",
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl sx={{ minWidth: 180, ...glassInput(theme) }}>
                                <InputLabel>Filter by state</InputLabel>
                                <Select
                                    value={stateFilter}
                                    label="Filter by state"
                                    onChange={(e) => setStateFilter(e.target.value)}
                                >
                                    <MenuItem value="">All States</MenuItem>
                                    {states.map((st) => (
                                        <MenuItem key={st} value={st}>
                                            {st}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <AnimatePresence>
                                {hasActiveFilters && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                    >
                                        <HapticButton
                                            variant="outlined"
                                            onClick={clearFilters}
                                            startIcon={<ClearIcon />}
                                            sx={{
                                                height: 56,
                                                borderRadius: 3,
                                                borderColor: alpha(theme.palette.error.main, 0.5),
                                                color: "error.main",
                                                "&:hover": {
                                                    borderColor: "error.main",
                                                    background: alpha(theme.palette.error.main, 0.08),
                                                },
                                            }}
                                        >
                                            Clear
                                        </HapticButton>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Box>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            sx={{
                                ...glassCard(theme),
                                "& .MuiToggleButton-root": {
                                    border: "none",
                                    px: 2,
                                    "&.Mui-selected": {
                                        background: alpha(theme.palette.primary.main, 0.15),
                                        color: "primary.main",
                                    },
                                    "&:active": { transform: "scale(0.95)" },
                                    transition: "all 150ms ease",
                                },
                            }}
                        >
                            <ToggleButton value="grid">
                                <Tooltip title="Card View">
                                    <GridViewIcon />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="table">
                                <Tooltip title="Table View">
                                    <TableRowsIcon />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Results count */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2, fontWeight: 500 }}
                    >
                        Showing{" "}
                        <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
                            {filtered.length}
                        </Box>{" "}
                        {filtered.length === 1 ? "district" : "districts"}
                        {hasActiveFilters && " matching your filters"}
                    </Typography>
                </Paper>
            </Container>

            {/* ═══════════════════════════════════════════════════════════════════
                CONTENT: GRID OR TABLE VIEW
                ═══════════════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg">
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress size={48} />
                    </Box>
                ) : viewMode === "grid" ? (
                    /* ─────────── CARD GRID VIEW ─────────── */
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: "repeat(4, 1fr)",
                            },
                            gap: 3,
                        }}
                    >
                        {filtered.map((d, idx) => (
                            <DistrictCard
                                key={d.apiid || d.DistrictName}
                                district={d}
                                index={idx}
                            />
                        ))}
                    </Box>
                ) : (
                    /* ─────────── MUI PRO DATA GRID VIEW ─────────── */
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Paper
                            sx={{
                                ...glassCard(theme),
                                borderRadius: 4,
                                overflow: "hidden",
                                height: 650,
                                "& .MuiDataGrid-root": {
                                    border: "none",
                                    "& .MuiDataGrid-columnHeaders": {
                                        background: alpha(theme.palette.primary.main, 0.06),
                                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                    },
                                    "& .MuiDataGrid-row": {
                                        "&:hover": {
                                            background: alpha(theme.palette.secondary.main, 0.06),
                                        },
                                    },
                                    "& .MuiDataGrid-cell:focus": {
                                        outline: `2px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                                        outlineOffset: -2,
                                    },
                                },
                            }}
                        >
                            <DataGridPro
                                rows={filtered}
                                columns={columns}
                                getRowId={(row) => row.apiid || row.DistrictName}
                                rowHeight={70}
                                disableRowSelectionOnClick
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 300 },
                                    },
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: "NoOfUsers", sort: "desc" }],
                                    },
                                    pagination: {
                                        paginationModel: { pageSize: 25 },
                                    },
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                                pagination
                                sx={{
                                    "& .MuiDataGrid-toolbarContainer": {
                                        p: 2,
                                        gap: 2,
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    },
                                }}
                            />
                        </Paper>
                    </motion.div>
                )}

                {/* Empty state */}
                {!loading && filtered.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Paper
                            sx={{
                                ...glassCard(theme),
                                p: 8,
                                textAlign: "center",
                                borderRadius: 4,
                            }}
                        >
                            <SchoolIcon
                                sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                No districts found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Try adjusting your search or filter criteria
                            </Typography>
                            <HapticButton
                                variant="contained"
                                onClick={clearFilters}
                                sx={{ borderRadius: 99, px: 4 }}
                            >
                                Clear Filters
                            </HapticButton>
                        </Paper>
                    </motion.div>
                )}
            </Container>

            {/* ═══════════════════════════════════════════════════════════════════
                CTA SECTION
                ═══════════════════════════════════════════════════════════════════ */}
            <Container maxWidth="md" sx={{ mt: 10 }}>
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Paper
                        sx={{
                            p: { xs: 4, md: 6 },
                            borderRadius: 5,
                            textAlign: "center",
                            background: `
                                radial-gradient(ellipse at 30% 0%, rgba(33, 219, 184, 0.12) 0%, transparent 50%),
                                linear-gradient(135deg, 
                                    ${alpha(theme.palette.primary.main, 0.04)} 0%, 
                                    ${alpha(theme.palette.secondary.main, 0.06)} 100%)
                            `,
                            ...glassCard(theme),
                            border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                mb: 2,
                                background: "linear-gradient(135deg, #003366 0%, #21dbb8 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            Ready to Transform Your District?
                        </Typography>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ mb: 4, maxWidth: 500, mx: "auto", fontWeight: 400 }}
                        >
                            Join the growing network of forward-thinking districts
                            leveraging Swan Solutions to drive educational excellence.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                            <HapticButton
                                variant="contained"
                                size="large"
                                href="/contact"
                                component={motion.a}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                sx={{
                                    borderRadius: 99,
                                    px: 5,
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontSize: "1.05rem",
                                    background: "linear-gradient(135deg, #145c8c 0%, #21dbb8 100%)",
                                    boxShadow: `0 8px 24px ${alpha("#21dbb8", 0.3)}`,
                                }}
                            >
                                Request a Demo
                            </HapticButton>
                            <HapticButton
                                variant="outlined"
                                size="large"
                                href="/solutions"
                                component={motion.a}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                sx={{
                                    borderRadius: 99,
                                    px: 5,
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontSize: "1.05rem",
                                    borderWidth: 2,
                                    borderColor: alpha(theme.palette.primary.main, 0.5),
                                    "&:hover": {
                                        borderWidth: 2,
                                        borderColor: theme.palette.primary.main,
                                        background: alpha(theme.palette.primary.main, 0.05),
                                    },
                                }}
                            >
                                Explore Solutions
                            </HapticButton>
                        </Box>
                    </Paper>
                </motion.div>
            </Container>
        </Box>
    );
}